import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../../components/button';
import Typography from '../../../../../components/typography';
import Input from '../../../../../components/input';
import geocodeAddress from '../../../../../services/georref';
import trashcanIcon from '../../../../../assets/images/trashcan.svg';

import './stepAddressValidation.scss';

const StepAddressValidation = ({
  next,
  back,
  dataShippings,
}) => {
  const [editableShippings, setEditableShippings] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [hasValidated, setHasValidated] = useState(false);
  const [instructionsErrors, setInstructionsErrors] = useState({});
  const [correctedAddresses, setCorrectedAddresses] = useState({});
  const [initiallyValidAddresses, setInitiallyValidAddresses] = useState({});

  const [addressOptions, setAddressOptions] = useState({});
  const [isLoadingAddresses, setIsLoadingAddresses] = useState({});

  useEffect(() => {
    if (dataShippings && dataShippings.length > 0) {
      setEditableShippings(dataShippings.map((shipping) => ({ ...shipping })));

      // Validar instrucciones de entrega al cargar los datos
      const errors = {};
      dataShippings.forEach((shipping, index) => {
        if (shipping.senderAddressComplement && shipping.senderAddressComplement.length > 50) {
          errors[`senderAddressComplement-${index}`] = 'Las instrucciones no deben superar los 50 caracteres';
        }
        if (shipping.receiverAddressComplement && shipping.receiverAddressComplement.length > 50) {
          errors[`receiverAddressComplement-${index}`] = 'Las instrucciones no deben superar los 50 caracteres';
        }
      });
      setInstructionsErrors(errors);
    }
  }, [dataShippings]);

  const debouncedGeocodeSearch = useCallback(
    (() => {
      const timeouts = {};
      return (address, city, key) => {
        if (timeouts[key]) {
          clearTimeout(timeouts[key]);
        }
        timeouts[key] = setTimeout(async () => {
          if (address && address.length > 3 && city) {
            setIsLoadingAddresses((prev) => ({ ...prev, [key]: true }));
            try {
              const data = await geocodeAddress({
                country: 'COL',
                city: city.split('-')[0],
                state: city.split('-')[1] || city.split('-')[0],
                address,
              });
              const results = data?.response || [];
              setAddressOptions((prev) => ({ ...prev, [key]: results }));
            } catch (error) {
              setAddressOptions((prev) => ({ ...prev, [key]: [] }));
            } finally {
              setIsLoadingAddresses((prev) => ({ ...prev, [key]: false }));
            }
          } else {
            setAddressOptions((prev) => ({ ...prev, [key]: [] }));
          }
        }, 1000);
      };
    })(),
    [],
  );

  const findAddressOptionByValue = (key, value) => {
    const options = addressOptions[key];
    if (!options || options.length === 0) return null;
    // Solo retornar si el valor coincide exactamente con la dirección de alguna opción
    return options.find((opt) => opt.address === value);
  };

  // Guardar automáticamente los datos de georreferenciación cuando se obtengan resultados
  useEffect(() => {
    if (Object.keys(addressOptions).length > 0) {
      setEditableShippings((prevShippings) => {
        const updatedShippings = [...prevShippings];
        let hasChanges = false;

        Object.keys(addressOptions).forEach((key) => {
          const options = addressOptions[key];
          if (options && options.length > 0) {
            const firstOption = options[0];
            const match = key.match(/(sender|receiver)-(\d+)/);

            if (match) {
              const [, type, indexStr] = match;
              const index = parseInt(indexStr, 10);

              if (type === 'sender' && updatedShippings[index]) {
                const currentAddress = updatedShippings[index].senderAddress;
                if (currentAddress
                    && firstOption.address === currentAddress
                    && !updatedShippings[index].senderLatitude) {
                  updatedShippings[index].senderLatitude = firstOption.lat;
                  updatedShippings[index].senderLongitude = firstOption.lng;
                  updatedShippings[index].senderPostalCode = firstOption.postal_code;
                  hasChanges = true;
                }
              } else if (type === 'receiver' && updatedShippings[index]) {
                const currentAddress = updatedShippings[index].receiverAddress;
                if (currentAddress
                    && firstOption.address === currentAddress
                    && !updatedShippings[index].receiverLatitude) {
                  updatedShippings[index].receiverLatitude = firstOption.lat;
                  updatedShippings[index].receiverLongitude = firstOption.lng;
                  updatedShippings[index].receiverPostalCode = firstOption.postal_code;
                  hasChanges = true;
                }
              }
            }
          }
        });

        return hasChanges ? updatedShippings : prevShippings;
      });
    }
  }, [addressOptions]);

  const deleteShipping = (index) => {
    const updatedShippings = editableShippings.filter((_, i) => i !== index);
    setEditableShippings(updatedShippings);

    // Reindexar errores de instrucciones
    setInstructionsErrors((prev) => {
      const newErrors = {};
      Object.keys(prev).forEach((key) => {
        const match = key.match(/(senderAddressComplement|receiverAddressComplement)-(\d+)/);
        if (match) {
          const [, field, oldIndex] = match;
          const oldIndexNum = parseInt(oldIndex, 10);
          if (oldIndexNum < index) {
            newErrors[`${field}-${oldIndexNum}`] = prev[key];
          } else if (oldIndexNum > index) {
            newErrors[`${field}-${oldIndexNum - 1}`] = prev[key];
          }
        }
      });
      return newErrors;
    });

    // Reindexar validaciones
    setValidationResults((prev) => {
      const filtered = prev.filter((r) => r.rowNum !== index + 1);
      return filtered.map((r) => ({
        ...r,
        rowNum: r.rowNum > index + 1 ? r.rowNum - 1 : r.rowNum,
      }));
    });

    // Reindexar opciones de dirección
    setAddressOptions((prev) => {
      const newOptions = {};
      Object.keys(prev).forEach((key) => {
        const match = key.match(/(sender|receiver)-(\d+)/);
        if (match) {
          const [, type, oldIndex] = match;
          const oldIndexNum = parseInt(oldIndex, 10);
          if (oldIndexNum < index) {
            newOptions[`${type}-${oldIndexNum}`] = prev[key];
          } else if (oldIndexNum > index) {
            newOptions[`${type}-${oldIndexNum - 1}`] = prev[key];
          }
        }
      });
      return newOptions;
    });

    // Reindexar isLoadingAddresses
    setIsLoadingAddresses((prev) => {
      const newLoading = {};
      Object.keys(prev).forEach((key) => {
        const match = key.match(/(sender|receiver)-(\d+)/);
        if (match) {
          const [, type, oldIndex] = match;
          const oldIndexNum = parseInt(oldIndex, 10);
          if (oldIndexNum < index) {
            newLoading[`${type}-${oldIndexNum}`] = prev[key];
          } else if (oldIndexNum > index) {
            newLoading[`${type}-${oldIndexNum - 1}`] = prev[key];
          }
        }
      });
      return newLoading;
    });

    // Reindexar correctedAddresses
    setCorrectedAddresses((prev) => {
      const newCorrected = {};
      Object.keys(prev).forEach((key) => {
        const match = key.match(/(sender|receiver)-(\d+)/);
        if (match) {
          const [, type, oldIndex] = match;
          const oldIndexNum = parseInt(oldIndex, 10);
          if (oldIndexNum < index) {
            newCorrected[`${type}-${oldIndexNum}`] = prev[key];
          } else if (oldIndexNum > index) {
            newCorrected[`${type}-${oldIndexNum - 1}`] = prev[key];
          }
        }
      });
      return newCorrected;
    });
  };

  const updateShippingAddress = (index, field, value) => {
    const updatedShippings = [...editableShippings];
    updatedShippings[index][field] = value;

    // Validar instrucciones de entrega
    if (field === 'senderAddressComplement' || field === 'receiverAddressComplement') {
      const errorKey = `${field}-${index}`;

      if (!value || value.trim().length === 0) {
        setInstructionsErrors((prev) => ({
          ...prev,
          [errorKey]: 'Las instrucciones de entrega son obligatorias',
        }));
      } else if (value.length > 50) {
        setInstructionsErrors((prev) => ({
          ...prev,
          [errorKey]: 'Las instrucciones no deben superar los 50 caracteres',
        }));
      } else {
        setInstructionsErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    }

    if (field === 'senderAddress') {
      const originCity = updatedShippings[index].shippingOriginDepartamentName
        && updatedShippings[index].shippingOriginName
        ? `${updatedShippings[index].shippingOriginDepartamentName}-${updatedShippings[index].shippingOriginName}`
        : updatedShippings[index].origin;

      const selectedOption = findAddressOptionByValue(`sender-${index}`, value);

      if (selectedOption) {
        if (selectedOption.address) {
          updatedShippings[index].senderAddress = selectedOption.address;
        }
        if (selectedOption.lat) {
          updatedShippings[index].senderLatitude = selectedOption.lat;
        }
        if (selectedOption.lng) {
          updatedShippings[index].senderLongitude = selectedOption.lng;
        }
        if (selectedOption.postal_code) {
          updatedShippings[index].senderPostalCode = selectedOption.postal_code;
        }
        setAddressOptions((prev) => ({ ...prev, [`sender-${index}`]: [] }));

        setValidationResults((prev) => {
          const filtered = prev.filter((r) => !(r.rowNum === index + 1 && r.type === 'sender'));
          return [
            ...filtered,
            {
              rowNum: index + 1,
              type: 'sender',
              isValid: true,
              georefData: {
                lat: selectedOption.lat,
                lng: selectedOption.lng,
                postal_code: selectedOption.postal_code,
              },
            },
          ];
        });

        // Marcar como dirección corregida para mantener la línea verde
        setCorrectedAddresses((prev) => ({ ...prev, [`sender-${index}`]: true }));
      } else if (value && value.length > 3) {
        // Si cambia la dirección pero no es una opción válida, marcar como inválida
        setValidationResults((prev) => {
          const filtered = prev.filter((r) => !(r.rowNum === index + 1 && r.type === 'sender'));
          return [
            ...filtered,
            {
              rowNum: index + 1,
              type: 'sender',
              isValid: false,
              error: 'Dirección no encontrada',
            },
          ];
        });
        // Remover de direcciones corregidas si se vuelve inválida
        setCorrectedAddresses((prev) => {
          const newCorrected = { ...prev };
          delete newCorrected[`sender-${index}`];
          return newCorrected;
        });
        debouncedGeocodeSearch(value, originCity, `sender-${index}`);
      }
    } else if (field === 'receiverAddress') {
      const destinyCity = updatedShippings[index].shippingDestinyDepartamentName
        && updatedShippings[index].shippingDestinyName
        ? `${updatedShippings[index].shippingDestinyDepartamentName}-${updatedShippings[index].shippingDestinyName}`
        : updatedShippings[index].destiny;

      const selectedOption = findAddressOptionByValue(`receiver-${index}`, value);

      if (selectedOption) {
        if (selectedOption.address) {
          updatedShippings[index].receiverAddress = selectedOption.address;
        }
        if (selectedOption.lat) {
          updatedShippings[index].receiverLatitude = selectedOption.lat;
        }
        if (selectedOption.lng) {
          updatedShippings[index].receiverLongitude = selectedOption.lng;
        }
        if (selectedOption.postal_code) {
          updatedShippings[index].receiverPostalCode = selectedOption.postal_code;
        }
        setAddressOptions((prev) => ({ ...prev, [`receiver-${index}`]: [] }));

        setValidationResults((prev) => {
          const filtered = prev.filter((r) => !(r.rowNum === index + 1 && r.type === 'receiver'));
          return [
            ...filtered,
            {
              rowNum: index + 1,
              type: 'receiver',
              isValid: true,
              georefData: {
                lat: selectedOption.lat,
                lng: selectedOption.lng,
                postal_code: selectedOption.postal_code,
              },
            },
          ];
        });

        // Marcar como dirección corregida para mantener la línea verde
        setCorrectedAddresses((prev) => ({ ...prev, [`receiver-${index}`]: true }));
      } else if (value && value.length > 3) {
        // Si cambia la dirección pero no es una opción válida, marcar como inválida
        setValidationResults((prev) => {
          const filtered = prev.filter((r) => !(r.rowNum === index + 1 && r.type === 'receiver'));
          return [
            ...filtered,
            {
              rowNum: index + 1,
              type: 'receiver',
              isValid: false,
              error: 'Dirección no encontrada',
            },
          ];
        });
        // Remover de direcciones corregidas si se vuelve inválida
        setCorrectedAddresses((prev) => {
          const newCorrected = { ...prev };
          delete newCorrected[`receiver-${index}`];
          return newCorrected;
        });
        debouncedGeocodeSearch(value, destinyCity, `receiver-${index}`);
      }
    }

    setEditableShippings(updatedShippings);
  };

  useEffect(() => {
    if (editableShippings && editableShippings.length > 0 && !hasValidated) {
      // Las direcciones ya fueron validadas al subir el archivo
      // Solo cargamos los resultados que vienen del paso anterior
      const preValidatedResults = [];
      editableShippings.forEach((shipping, index) => {
        // Marcar como válida si tiene coordenadas
        if (shipping.senderLatitude && shipping.senderLongitude) {
          preValidatedResults.push({
            rowNum: index + 1,
            type: 'sender',
            isValid: true,
            georefData: {
              lat: shipping.senderLatitude,
              lng: shipping.senderLongitude,
              postal_code: shipping.senderPostalCode,
            },
          });
        } else {
          // Marcar como inválida si no tiene coordenadas
          preValidatedResults.push({
            rowNum: index + 1,
            type: 'sender',
            isValid: false,
            error: 'Dirección no encontrada',
          });
        }

        if (shipping.receiverLatitude && shipping.receiverLongitude) {
          preValidatedResults.push({
            rowNum: index + 1,
            type: 'receiver',
            isValid: true,
            georefData: {
              lat: shipping.receiverLatitude,
              lng: shipping.receiverLongitude,
              postal_code: shipping.receiverPostalCode,
            },
          });
        } else {
          // Marcar como inválida si no tiene coordenadas
          preValidatedResults.push({
            rowNum: index + 1,
            type: 'receiver',
            isValid: false,
            error: 'Dirección no encontrada',
          });
        }
      });
      setValidationResults(preValidatedResults);

      // Marcar las direcciones que estaban válidas desde el inicio (del Excel)
      const initiallyValid = {};
      preValidatedResults.forEach((result) => {
        if (result.isValid) {
          initiallyValid[`${result.type}-${result.rowNum - 1}`] = true;
        }
      });
      setInitiallyValidAddresses(initiallyValid);

      setHasValidated(true);
    }
  }, [editableShippings, hasValidated]);

  let enviosValidos = 0;
  let enviosInvalidos = 0;

  editableShippings.forEach((_, index) => {
    const senderValidation = validationResults.find(
      (r) => r.rowNum === index + 1 && r.type === 'sender',
    );
    const receiverValidation = validationResults.find(
      (r) => r.rowNum === index + 1 && r.type === 'receiver',
    );

    const hasSenderInstructionsError = instructionsErrors[`senderAddressComplement-${index}`];
    const hasReceiverInstructionsError = instructionsErrors[`receiverAddressComplement-${index}`];

    // Un envío es válido solo si TODAS sus direcciones e instrucciones son válidas
    const senderIsValid = (!senderValidation || senderValidation.isValid)
      && !hasSenderInstructionsError;
    const receiverIsValid = (!receiverValidation || receiverValidation.isValid)
      && !hasReceiverInstructionsError;

    if (senderIsValid && receiverIsValid) {
      enviosValidos += 1;
    } else {
      enviosInvalidos += 1;
    }
  });

  const validCount = enviosValidos;
  const invalidCount = enviosInvalidos;

  return (
    <div className="step-address-validation">
      <div className="step-address-validation__header">
        <Typography type="h2" className="step-address-validation__title">
          Resumen
        </Typography>
      </div>

      <div className="step-address-validation__content">
        <div className="step-address-validation__summary">
          <div className="step-address-validation__summary-stats">
            <div className="step-address-validation__stat step-address-validation__stat--valid">
              <Typography type="p" className="step-address-validation__stat-label">
                Pedidos SIN novedad
              </Typography>
              <Typography type="p" className="step-address-validation__stat-value">
                {validCount}
              </Typography>
            </div>
            <div className="step-address-validation__stat step-address-validation__stat--invalid">
              <Typography type="p" className="step-address-validation__stat-label">
                Pedidos CON novedad
              </Typography>
              <Typography type="p" className="step-address-validation__stat-value">
                {invalidCount}
              </Typography>
            </div>
          </div>

          {invalidCount > 0 && (
            <Typography type="p" className="step-address-validation__error-message">
              Revisa los campos señalados y corrige la información para continuar con el proceso
            </Typography>
          )}
        </div>

        <div className="step-address-validation__address-table">
          <div className="step-address-validation__table-header">
            <div className="step-address-validation__table-column">
              <Typography type="p" className="step-address-validation__column-title">
                Origen
              </Typography>
            </div>
            <div className="step-address-validation__table-column">
              <Typography type="p" className="step-address-validation__column-title">
                Destino
              </Typography>
            </div>
          </div>

          <div className="step-address-validation__table-subheader">
            <div className="step-address-validation__subheader-columns">
              <Typography type="p" className="step-address-validation__subheader-cell">Ciudad</Typography>
              <Typography type="p" className="step-address-validation__subheader-cell">Dirección</Typography>
              <Typography type="p" className="step-address-validation__subheader-cell">Instrucciones de entrega</Typography>
            </div>
            <div className="step-address-validation__subheader-columns">
              <Typography type="p" className="step-address-validation__subheader-cell">Ciudad</Typography>
              <Typography type="p" className="step-address-validation__subheader-cell">Dirección</Typography>
              <Typography type="p" className="step-address-validation__subheader-cell">Instrucciones de entrega</Typography>
            </div>
          </div>

          <div className="step-address-validation__table-body">
            {editableShippings.map((shipping, index) => {
              const senderValidation = validationResults.find(
                (r) => r.rowNum === index + 1 && r.type === 'sender',
              );
              const receiverValidation = validationResults.find(
                (r) => r.rowNum === index + 1 && r.type === 'receiver',
              );

              const originCity = shipping.shippingOriginDepartamentName
                && shipping.shippingOriginName
                ? `${shipping.shippingOriginDepartamentName}-${shipping.shippingOriginName}`
                : shipping.origin;

              const destinyCity = shipping.shippingDestinyDepartamentName
                && shipping.shippingDestinyName
                ? `${shipping.shippingDestinyDepartamentName}-${shipping.shippingDestinyName}`
                : shipping.destiny;

              const senderOptions = addressOptions[`sender-${index}`]
                ? addressOptions[`sender-${index}`].map((option) => option.address)
                : [];

              const receiverOptions = addressOptions[`receiver-${index}`]
                ? addressOptions[`receiver-${index}`].map((option) => option.address)
                : [];

              return (
                <div key={`shipping-${shipping.id || index}`} className="step-address-validation__table-row">
                  <div className="step-address-validation__table-column">
                    <div className="step-address-validation__cell-group">
                      <Typography
                        type="p"
                        className="step-address-validation__cell step-address-validation__cell--city"
                      >
                        {originCity || ''}
                      </Typography>
                      <Input
                        value={shipping.senderAddress || ''}
                        onChange={(value) => updateShippingAddress(index, 'senderAddress', value)}
                        dataList={senderOptions}
                        isLoading={isLoadingAddresses[`sender-${index}`]}
                        annotationError={
                          senderValidation && !senderValidation.isValid
                            ? senderValidation.error
                            : null
                        }
                        className={`step-address-validation__input ${senderValidation && senderValidation.isValid ? 'input-valid' : ''}`}
                        inputColor={correctedAddresses[`sender-${index}`] && (!senderValidation || senderValidation.isValid) ? 'line-focused-valid' : 'line-focused'}
                        forceGreenLine={correctedAddresses[`sender-${index}`] && (!senderValidation || senderValidation.isValid)}
                        inactiveDataList
                        disabled={initiallyValidAddresses[`sender-${index}`]}
                      />
                      <Input
                        value={shipping.senderAddressComplement || ''}
                        onChange={(value) => updateShippingAddress(index, 'senderAddressComplement', value)}
                        placeholder="Instrucciones de entrega"
                        className="step-address-validation__input"
                        annotationError={instructionsErrors[`senderAddressComplement-${index}`]}
                      />
                    </div>
                  </div>

                  <div className="step-address-validation__table-column">
                    <div className="step-address-validation__cell-group">
                      <Typography
                        type="p"
                        className="step-address-validation__cell step-address-validation__cell--city"
                      >
                        {destinyCity || ''}
                      </Typography>
                      <Input
                        value={shipping.receiverAddress || ''}
                        onChange={(value) => updateShippingAddress(index, 'receiverAddress', value)}
                        dataList={receiverOptions}
                        isLoading={isLoadingAddresses[`receiver-${index}`]}
                        annotationError={
                          receiverValidation && !receiverValidation.isValid
                            ? receiverValidation.error
                            : null
                        }
                        className={`step-address-validation__input ${receiverValidation && receiverValidation.isValid ? 'input-valid' : ''}`}
                        inputColor={correctedAddresses[`receiver-${index}`] && (!receiverValidation || receiverValidation.isValid) ? 'line-focused-valid' : 'line-focused'}
                        forceGreenLine={correctedAddresses[`receiver-${index}`] && (!receiverValidation || receiverValidation.isValid)}
                        inactiveDataList
                        disabled={initiallyValidAddresses[`receiver-${index}`]}
                      />
                      <Input
                        value={shipping.receiverAddressComplement || ''}
                        onChange={(value) => updateShippingAddress(index, 'receiverAddressComplement', value)}
                        placeholder="Instrucciones de entrega"
                        className="step-address-validation__input"
                        annotationError={instructionsErrors[`receiverAddressComplement-${index}`]}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="step-address-validation__delete-button"
                    onClick={() => deleteShipping(index)}
                    title="Eliminar envío"
                  >
                    <img src={trashcanIcon} alt="Eliminar" className="step-address-validation__delete-icon" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="step-address-validation__actions">
        {back && (
          <Button
            text="Volver"
            onClick={back}
            type="secondary"
            className="step-address-validation__back-button"
          />
        )}
        <Button
          text="Continuar"
          onClick={() => next(editableShippings)}
          disabled={invalidCount > 0 || Object.keys(instructionsErrors).length > 0}
          type="primary"
          className="step-address-validation__next-button"
        />
      </div>
    </div>
  );
};

StepAddressValidation.propTypes = {
  next: PropTypes.func.isRequired,
  back: PropTypes.func,
  dataShippings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

StepAddressValidation.defaultProps = {
  back: null,
};

export default StepAddressValidation;
